import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence, LazyMotion, domAnimation } from 'framer-motion';
import { FaPen } from '@react-icons/all-files/fa/FaPen';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { FaChartLine } from '@react-icons/all-files/fa/FaChartLine';
import { FaUsers } from '@react-icons/all-files/fa/FaUsers';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaBars } from '@react-icons/all-files/fa/FaBars';
import { useTranslation } from 'react-i18next';
import GoogleSignIn from './components/GoogleSignIn';
import FileUpload from './components/FileUpload';
import './i18n';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Header = styled.header`
  padding: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const Logo = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #2563eb;
`;

const LanguageSwitcher = styled.div`
  display: flex;
  gap: 0;
  margin-right: 12px;

  @media (max-width: 768px) {
    position: absolute;
    right: 40px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1001;
    display: none;
    margin-right: 0;
  }
`;

const LanguageSwitcherMobile = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    gap: 0;
    margin-right: 0;
    position: absolute;
    right: 100px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1001;
  }
`;

const Nav = styled.nav<{ isMenuOpen?: boolean }>`
  display: flex;
  gap: 20px;
  align-items: center;
  height: 100%;
  flex-wrap: nowrap;

  @media (max-width: 768px) {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    padding: 0 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: ${props => props.isMenuOpen ? 'flex' : 'none'};
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    z-index: 1000;
    border-radius: 8px;
    min-width: fit-content;
    backdrop-filter: blur(8px);
    background-color: rgba(255, 255, 255, 0.95);
    width: auto;
    min-height: fit-content;
    line-height: 1;
  }
`;

const NavLink = styled.a`
  color: #4b5563;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  white-space: nowrap;
  line-height: 1;

  @media (max-width: 768px) {
    padding: 0;
    width: fit-content;
    margin: 0;
    height: 40px;
    font-size: 15px;
    letter-spacing: 0.3px;
  }

  &:hover {
    color: #2563eb;
    transform: translateX(2px);
  }
`;

const Hero = styled.section`
  padding: 80px 0;
  text-align: center;
`;

const Title = styled(motion.h1)`
  font-size: 48px;
  font-weight: 800;
  color: #1f2937;
  margin-bottom: 20px;
`;

const Subtitle = styled(motion.p)`
  font-size: 20px;
  color: #4b5563;
  max-width: 600px;
  margin: 0 auto 40px;
`;

const CTAButton = styled(motion.button)`
  background-color: #2563eb;
  color: white;
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #1d4ed8;
  }
`;

const Features = styled.section`
  padding: 80px 0;
  background-color: #f3f4f6;
  margin: 0 -20px;
  padding: 80px 20px;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-top: 40px;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const FeatureIcon = styled.div`
  font-size: 40px;
  color: #2563eb;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FeatureTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #1f2937;
`;

const FeatureDescription = styled.p`
  color: #4b5563;
  line-height: 1.6;
`;

const LanguageButton = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  color: ${props => props.active ? '#2563eb' : '#6b7280'};
  cursor: pointer;
  font-weight: ${props => props.active ? '600' : '500'};
  padding: 5px 8px;
  border-radius: 4px;
  transition: all 0.3s;
  margin: 0 -2px;
  font-size: 14px;

  &:hover {
    color: #2563eb;
  }
`;

const ModalWrapper = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: white;
  padding: 40px;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  color: #4b5563;
  transition: color 0.3s;

  &:hover {
    color: #2563eb;
  }
`;

const AboutTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 20px;
`;

const AboutText = styled.div`
  color: #4b5563;
  line-height: 1.8;
  white-space: pre-line;

  ul {
    margin: 20px 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 10px;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 0;

  @media (max-width: 768px) {
    margin-left: 0;
    position: absolute;
    right: 50px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1001;
  }
`;

const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  transition: all 0.3s ease;

  &:hover {
    border-color: #2563eb;
  }
`;

const UserName = styled.span`
  color: #6b7280;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.025em;

  @media (max-width: 768px) {
    display: none;
  }
`;

const LogoutButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  margin-left: 8px;
  transition: color 0.3s;

  @media (max-width: 768px) {
    display: none;
  }

  &:hover {
    color: #2563eb;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #4b5563;
  cursor: pointer;
  padding: 5px;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
  margin-left: auto;

  @media (max-width: 768px) {
    display: flex;
    margin-left: 0;
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }

  &:hover {
    color: #2563eb;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 20px;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 18px;
  text-align: center;
`;

const RetryButton = styled.button`
  background-color: #2563eb;
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s;

  &:hover {
    background-color: #1d4ed8;
  }
`;

const Pricing = styled.section`
  padding: 80px 0;
  background-color: white;
  margin: 0 -20px;
  padding: 80px 20px;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 40px;
`;

const PricingCard = styled(motion.div)`
  background: white;
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
`;

const PricingTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #1f2937;
`;

const PricingPrice = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: #2563eb;
  margin-bottom: 20px;
`;

const PricingPeriod = styled.span`
  font-size: 16px;
  color: #6b7280;
  font-weight: 500;
`;

const PricingFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
  text-align: left;
  flex-grow: 1;
`;

const PricingFeature = styled.li`
  color: #4b5563;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;

  &:before {
    content: "✓";
    color: #10b981;
    font-weight: bold;
  }
`;

const PricingButton = styled(motion.button)`
  background-color: #2563eb;
  color: white;
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;
  margin-top: auto;

  &:hover {
    background-color: #1d4ed8;
  }
`;

const GoogleSignInWrapper = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

function App() {
  const { t, i18n } = useTranslation();
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);

  useEffect(() => {
    // Check if Google Client ID is configured
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      setError('Google Client ID is not configured. Please check your .env file.');
    }
  }, []);

  const handleContactClick = () => {
    window.location.href = 'mailto:calvin.tang@gmail.com';
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const handleGoogleSuccess = async (response: any) => {
    try {
      if (!response.userInfo) {
        throw new Error('No credential received from Google');
      }

      const userData = response.userInfo;
      setUser({
        name: userData.name,
        picture: userData.picture,
        email: userData.email
      });
      setError(null);
    } catch (error) {
      console.error('Error handling Google Sign-In:', error);
      setError('Failed to sign in with Google. Please try again.');
    }
  };

  const handleGoogleError = () => {
    setError('Google Sign-In failed. Please try again.');
  };

  const handleSignOut = () => {
    setUser(null);
    setIsMenuOpen(false);
    // Clear the stored session
    localStorage.removeItem('googleSession');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleGetStarted = () => {
    if (user) {
      setShowFileUpload(true);
    } else {
      // Show Google Sign-In modal or redirect to sign-in
      // For now, we'll just show the Google Sign-In component
      setShowFileUpload(false);
    }
  };

  const handleUploadSuccess = (fileInfo: { fileUrl: string; extractedText: string[] }) => {
    console.log('File uploaded successfully:', fileInfo.fileUrl);
    console.log('Extracted text:', fileInfo.extractedText);
    // You can add additional logic here to handle the extracted text
  };

  const handleUploadError = (error: string) => {
    console.error('Upload error:', error);
    // TODO: Show error message to user
  };

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <ErrorMessage>{error}</ErrorMessage>
          <RetryButton onClick={() => window.location.reload()}>Retry</RetryButton>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Logo>{t('logo')}</Logo>
        <Nav isMenuOpen={isMenuOpen}>
          <NavLink href="#features">{t('features')}</NavLink>
          <NavLink href="#pricing">{t('pricing')}</NavLink>
          <NavLink onClick={() => setIsAboutOpen(true)} style={{ cursor: 'pointer' }}>{t('about')}</NavLink>
          <NavLink onClick={handleContactClick} style={{ cursor: 'pointer' }}>{t('contact')}</NavLink>
          {!user && (
            <GoogleSignInWrapper>
              <GoogleSignIn 
                onSuccess={handleGoogleSuccess} 
                onError={handleGoogleError} 
                user={user}
                onSignOut={handleSignOut}
              />
            </GoogleSignInWrapper>
          )}
          {user && window.innerWidth <= 768 && (
            <NavLink onClick={handleSignOut} style={{ cursor: 'pointer' }}>
              {i18n.language === 'zh' ? '登出' : 'Sign Out'}
            </NavLink>
          )}
        </Nav>
        {user && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <LanguageSwitcher>
              <LanguageButton 
                active={i18n.language === 'en'} 
                onClick={() => changeLanguage('en')}
              >
                EN
              </LanguageButton>
              <LanguageButton 
                active={i18n.language === 'zh'} 
                onClick={() => changeLanguage('zh')}
              >
                繁
              </LanguageButton>
            </LanguageSwitcher>
            <UserMenu>
              <UserAvatar src={user.picture} alt={user.name} />
              <UserName>{user.name.split(' ')[0]}</UserName>
              <LogoutButton onClick={handleSignOut}>
                {i18n.language === 'zh' ? '登出' : 'Sign Out'}
              </LogoutButton>
            </UserMenu>
          </div>
        )}
        <LanguageSwitcherMobile>
          <LanguageButton 
            active={i18n.language === 'en'} 
            onClick={() => changeLanguage('en')}
          >
            EN
          </LanguageButton>
          <LanguageButton 
            active={i18n.language === 'zh'} 
            onClick={() => changeLanguage('zh')}
          >
            繁
          </LanguageButton>
        </LanguageSwitcherMobile>
        <MenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <FaBars size={24} />
        </MenuButton>
      </Header>

      <Hero>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {i18n.language === 'zh' ? '智能作文評分系統' : 'AI Composition Marking'}
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {i18n.language === 'zh' 
            ? '上傳您的作文掃描檔（PDF或圖片），系統將自動評分（0-100分）並提供詳細反饋'
            : 'Upload your scanned composition (PDF or image) and get instant AI-powered marking (0-100 scale) with detailed feedback'}
        </Subtitle>
        {showFileUpload ? (
          <FileUpload
            onUploadSuccess={handleUploadSuccess}
            onUploadError={handleUploadError}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            {!user && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                style={{ 
                  color: '#374151',
                  fontSize: '15px',
                  marginBottom: '16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '20px 36px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  border: '1px solid rgba(229, 231, 235, 0.8)',
                  maxWidth: '500px',
                  minWidth: '400px',
                  textAlign: 'center',
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap',
                  fontWeight: '500',
                  letterSpacing: '0.025em',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'linear-gradient(90deg, #3b82f6, #10b981)',
                  opacity: 0.8
                }} />
                {i18n.language === 'zh' 
                  ? '請先登入以開始使用評分系統'
                  : 'Please sign in to start using the marking system'}
              </motion.div>
            )}
            {user ? (
              <CTAButton
                onClick={handleGetStarted}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {i18n.language === 'zh' ? '開始評分' : 'Start Marking'}
              </CTAButton>
            ) : (
              <GoogleSignIn 
                onSuccess={handleGoogleSuccess} 
                onError={handleGoogleError} 
                user={user}
                onSignOut={handleSignOut}
              />
            )}
          </div>
        )}
      </Hero>

      <Features>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {t('keyFeatures')}
        </Title>
        <FeatureGrid>
          <FeatureCard
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <FeatureIcon>
              <FaPen size={24} />
            </FeatureIcon>
            <FeatureTitle>{t('smartGrading')}</FeatureTitle>
            <FeatureDescription>
              {t('smartGradingDesc')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <FeatureIcon>
              <FaCheck size={24} />
            </FeatureIcon>
            <FeatureTitle>{t('detailedFeedback')}</FeatureTitle>
            <FeatureDescription>
              {t('detailedFeedbackDesc')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <FeatureIcon>
              <FaChartLine size={24} />
            </FeatureIcon>
            <FeatureTitle>{t('progressTracking')}</FeatureTitle>
            <FeatureDescription>
              {t('progressTrackingDesc')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <FeatureIcon>
              <FaUsers size={24} />
            </FeatureIcon>
            <FeatureTitle>{t('collaborativeReview')}</FeatureTitle>
            <FeatureDescription>
              {t('collaborativeReviewDesc')}
            </FeatureDescription>
          </FeatureCard>
        </FeatureGrid>
      </Features>

      <Pricing id="pricing">
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {t('pricingTitle')}
        </Title>
        <PricingGrid>
          <PricingCard
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <PricingTitle>{t('freePlan')}</PricingTitle>
            <PricingPrice>
              {t('free')}
              <PricingPeriod>/{t('month')}</PricingPeriod>
            </PricingPrice>
            <PricingFeatures>
              <PricingFeature>{t('freeFeature1')}</PricingFeature>
              <PricingFeature>{t('freeFeature2')}</PricingFeature>
              <PricingFeature>{t('freeFeature3')}</PricingFeature>
            </PricingFeatures>
            <PricingButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('getStarted')}
            </PricingButton>
          </PricingCard>

          <PricingCard
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3 }}
            style={{ transform: 'scale(1.05)', border: '2px solid #2563eb' }}
          >
            <PricingTitle>{t('proPlan')}</PricingTitle>
            <PricingPrice>
              $9.99
              <PricingPeriod>/{t('month')}</PricingPeriod>
            </PricingPrice>
            <PricingFeatures>
              <PricingFeature>{t('proFeature1')}</PricingFeature>
              <PricingFeature>{t('proFeature2')}</PricingFeature>
              <PricingFeature>{t('proFeature3')}</PricingFeature>
              <PricingFeature>{t('proFeature4')}</PricingFeature>
            </PricingFeatures>
            <PricingButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('getStarted')}
            </PricingButton>
          </PricingCard>

          <PricingCard
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <PricingTitle>{t('enterprisePlan')}</PricingTitle>
            <PricingPrice>
              {t('custom')}
            </PricingPrice>
            <PricingFeatures>
              <PricingFeature>{t('enterpriseFeature1')}</PricingFeature>
              <PricingFeature>{t('enterpriseFeature2')}</PricingFeature>
              <PricingFeature>{t('enterpriseFeature3')}</PricingFeature>
              <PricingFeature>{t('enterpriseFeature4')}</PricingFeature>
              <PricingFeature>{t('enterpriseFeature5')}</PricingFeature>
            </PricingFeatures>
            <PricingButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('contactUs')}
            </PricingButton>
          </PricingCard>
        </PricingGrid>
      </Pricing>

      <AnimatePresence initial={false}>
        {isAboutOpen && (
          <ModalWrapper
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsAboutOpen(false)}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <CloseButton onClick={() => setIsAboutOpen(false)}>
                <FaTimes size={24} />
              </CloseButton>
              <AboutTitle>{t('aboutTitle')}</AboutTitle>
              <AboutText>{t('aboutContent')}</AboutText>
            </ModalContent>
          </ModalWrapper>
        )}
      </AnimatePresence>
    </Container>
  );
}

export default App;
